import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('ezvi-cookie-consent');
    if (!consent) {
      // Show popup after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      preferences: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('ezvi-cookie-consent', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      preferences: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('ezvi-cookie-consent', JSON.stringify(necessaryOnly));
    setIsVisible(false);
  };

  const savePreferences = () => {
    const customPrefs = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('ezvi-cookie-consent', JSON.stringify(customPrefs));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}}
      />
      
      {/* Cookie Banner */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-[rgba(0,0,0,0.1)] pointer-events-auto overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8FEC78] to-[#5BC943] flex items-center justify-center">
              <Cookie size={20} className="text-white" />
            </div>
            <h3 className="font-semibold text-[rgb(0,55,32)] text-lg">Informativa Cookie</h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {!showPreferences ? (
            <>
              <p className="text-[rgb(131,146,140)] mb-4">
                Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. 
                Alcuni cookie sono necessari per il funzionamento del sito, mentre altri ci aiutano 
                a capire come lo utilizzi.
              </p>
              <p className="text-[rgb(131,146,140)] text-sm mb-4">
                Per maggiori informazioni, consulta la nostra{' '}
                <Link to="/cookies" className="text-[rgb(13,121,22)] hover:underline">Cookie Policy</Link>
                {' '}e la{' '}
                <Link to="/privacy" className="text-[rgb(13,121,22)] hover:underline">Privacy Policy</Link>.
              </p>
            </>
          ) : (
            <div className="space-y-4 mb-4">
              {/* Necessary */}
              <div className="flex items-center justify-between p-3 bg-[rgba(0,0,0,0.02)] rounded-lg">
                <div>
                  <h4 className="font-medium text-[rgb(0,55,32)]">Cookie Necessari</h4>
                  <p className="text-sm text-[rgb(131,146,140)]">Essenziali per il funzionamento del sito</p>
                </div>
                <div className="w-12 h-6 bg-[#8FEC78] rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full shadow" />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-3 bg-[rgba(0,0,0,0.02)] rounded-lg">
                <div>
                  <h4 className="font-medium text-[rgb(0,55,32)]">Cookie Analitici</h4>
                  <p className="text-sm text-[rgb(131,146,140)]">Ci aiutano a migliorare il servizio</p>
                </div>
                <button 
                  onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.analytics ? 'bg-[#8FEC78] justify-end' : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full shadow" />
                </button>
              </div>

              {/* Preferences */}
              <div className="flex items-center justify-between p-3 bg-[rgba(0,0,0,0.02)] rounded-lg">
                <div>
                  <h4 className="font-medium text-[rgb(0,55,32)]">Cookie di Preferenza</h4>
                  <p className="text-sm text-[rgb(131,146,140)]">Memorizzano le tue impostazioni</p>
                </div>
                <button 
                  onClick={() => setPreferences(p => ({ ...p, preferences: !p.preferences }))}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.preferences ? 'bg-[#8FEC78] justify-end' : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full shadow" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-[rgba(0,0,0,0.05)] bg-[rgba(0,0,0,0.02)]">
          {!showPreferences ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptNecessary}
                className="flex-1 px-4 py-3 text-[rgb(0,55,32)] font-medium bg-white border border-[rgba(0,0,0,0.1)] rounded-xl hover:bg-[rgba(0,0,0,0.02)] transition-colors"
              >
                Solo Necessari
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="flex-1 px-4 py-3 text-[rgb(0,55,32)] font-medium bg-white border border-[rgba(0,0,0,0.1)] rounded-xl hover:bg-[rgba(0,0,0,0.02)] transition-colors"
              >
                Personalizza
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 px-4 py-3 text-white font-medium bg-gradient-to-r from-[#8FEC78] to-[#5BC943] rounded-xl hover:shadow-lg hover:shadow-[#8FEC78]/30 transition-all"
              >
                Accetta Tutti
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowPreferences(false)}
                className="flex-1 px-4 py-3 text-[rgb(131,146,140)] font-medium hover:text-[rgb(0,55,32)] transition-colors"
              >
                Indietro
              </button>
              <button
                onClick={savePreferences}
                className="flex-1 px-4 py-3 text-white font-medium bg-gradient-to-r from-[#8FEC78] to-[#5BC943] rounded-xl hover:shadow-lg hover:shadow-[#8FEC78]/30 transition-all"
              >
                Salva Preferenze
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
